import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, MapPin, Calendar, MessageCircle, Shield, Package, User, TrendingUp, Clock, ChevronRight } from 'lucide-react';
import Layout from '../components/layout/Layout';
import ProductCard from '../components/product/ProductCard';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEO from '../components/common/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SellerProfilePage = () => {
  const { sellerId } = useParams();
  const navigate = useNavigate();
  const { getUserById, user } = useApp();
  const [seller, setSeller] = useState(null);
  const [sellerProducts, setSellerProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const loadSeller = async () => {
      setLoading(true);
      try {
        const sellerData = await getUserById(sellerId);
        setSeller(sellerData);
        // Fetch products from API for accurate data
        const res = await fetch(`${API_URL}/api/products?seller_id=${sellerId}&limit=100`);
        if (res.ok) {
          const products = await res.json();
          setSellerProducts(products.filter(p => p.seller_id === sellerId || p.sellerId === sellerId));
        }
      } catch (error) {
        console.error('Error loading seller:', error);
      } finally {
        setLoading(false);
      }
    };
    if (sellerId) loadSeller();
  }, [sellerId, getUserById]);

  const getAvatarUrl = (s) => {
    if (s?.avatar && !s.avatar.includes('undefined')) return s.avatar;
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(s?.name || 'User')}&background=1a3a2a&color=fff&size=200`;
  };

  const formatDate = (d) => {
    if (!d) return 'Recently';
    try {
      return new Date(d).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
    } catch { return 'Recently'; }
  };

  if (loading) {
    return (
      <Layout title="Seller" showBack showSearch={false}>
        <div className="flex flex-col items-center justify-center py-20">
          <div className="animate-spin w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  if (!seller) {
    return (
      <Layout title="Seller" showBack showSearch={false}>
        <div className="flex flex-col items-center justify-center py-20">
          <User className="w-16 h-16 text-stone-300 mb-4" />
          <p className="text-stone-500">Seller not found</p>
        </div>
      </Layout>
    );
  }

  const isOwnProfile = user?.id === seller.id;
  const memberSince = formatDate(seller.created_at || seller.joinedDate || seller.joined_date);
  const salesCount = seller.salesCount || seller.sales_count || 0;
  const rating = seller.rating || 0;
  const reviewCount = seller.reviewCount || seller.review_count || 0;
  const activeProducts = sellerProducts.filter(p => !p.sold);
  const soldProducts = sellerProducts.filter(p => p.sold);
  const displayProducts = filter === 'sold' ? soldProducts : filter === 'active' ? activeProducts : sellerProducts;

  return (
    <Layout showBack showSearch={false}>
      <SEO
        title={`${seller.name || 'Seller'} - ${sellerProducts.length} items for sale`}
        description={`Shop from ${seller.name || 'this seller'} on Minted. ${seller.location ? `Located in ${seller.location}.` : ''}`}
        image={getAvatarUrl(seller)}
        url={`/seller/${sellerId}`}
      />

      {/* Premium storefront header */}
      <div className="relative overflow-hidden" data-testid="seller-storefront-header">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900 via-emerald-800 to-stone-900" />
        <div className="absolute inset-0 opacity-5" style={{backgroundImage: 'radial-gradient(circle at 30% 40%, #fff 1px, transparent 1px)', backgroundSize: '30px 30px'}} />

        <div className="relative px-4 pt-6 pb-8">
          {/* Avatar + Name */}
          <div className="flex items-start gap-4 mb-5">
            <div className="relative">
              <img
                src={getAvatarUrl(seller)}
                alt={seller.name}
                className="w-20 h-20 rounded-2xl object-cover border-2 border-white/20 shadow-lg"
                data-testid="seller-avatar"
              />
              {seller.verified && (
                <div className="absolute -bottom-1 -right-1 bg-emerald-400 text-white p-1 rounded-full border-2 border-emerald-900">
                  <Shield className="w-3 h-3" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-display text-white truncate" data-testid="seller-name">{seller.name}</h1>
                {(seller.is_founding_seller || seller.isFoundingSeller) && (
                  <Badge className="bg-amber-500/20 text-amber-300 text-[9px] border border-amber-500/30">Founding</Badge>
                )}
              </div>
              {seller.bio && <p className="text-emerald-200 text-xs mt-1 line-clamp-2">{seller.bio}</p>}
              <div className="flex items-center gap-3 mt-2 text-emerald-300 text-[11px]">
                {seller.location && (
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{seller.location}</span>
                )}
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Joined {memberSince}</span>
              </div>
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl py-3 text-center">
              <p className="text-xl font-bold text-white">{activeProducts.length}</p>
              <p className="text-[10px] text-emerald-300">Active</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl py-3 text-center">
              <p className="text-xl font-bold text-white">{salesCount}</p>
              <p className="text-[10px] text-emerald-300">Sales</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl py-3 text-center">
              <p className="text-xl font-bold text-white flex items-center justify-center gap-0.5">
                {rating > 0 ? rating.toFixed(1) : '—'}
                {rating > 0 && <Star className="w-3 h-3 fill-amber-400 text-amber-400" />}
              </p>
              <p className="text-[10px] text-emerald-300">Rating</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl py-3 text-center">
              <p className="text-xl font-bold text-white">{soldProducts.length}</p>
              <p className="text-[10px] text-emerald-300">Sold</p>
            </div>
          </div>

          {/* Actions */}
          {!isOwnProfile && (
            <div className="flex gap-2 mt-4">
              <Button
                className="flex-1 bg-white text-emerald-900 hover:bg-emerald-50 h-10 text-sm rounded-xl"
                onClick={() => navigate('/messages')}
                data-testid="message-seller-btn"
              >
                <MessageCircle className="w-4 h-4 mr-1.5" /> Message
              </Button>
            </div>
          )}
          {isOwnProfile && (
            <Button
              variant="outline"
              className="w-full mt-4 border-white/20 text-white hover:bg-white/10 h-10 text-sm rounded-xl"
              onClick={() => navigate('/seller/analytics')}
            >
              <TrendingUp className="w-4 h-4 mr-1.5" /> View Analytics
            </Button>
          )}
        </div>
      </div>

      {/* Listings section */}
      <div className="px-4 pt-4 pb-24">
        {/* Filter tabs */}
        <div className="flex items-center gap-2 mb-4">
          {[
            { key: 'all', label: `All (${sellerProducts.length})` },
            { key: 'active', label: `Active (${activeProducts.length})` },
            { key: 'sold', label: `Sold (${soldProducts.length})` },
          ].map(f => (
            <button
              key={f.key}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                filter === f.key ? 'bg-emerald-800 text-white' : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
              }`}
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {displayProducts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 bg-stone-50 rounded-2xl">
            <Package className="w-12 h-12 text-stone-200 mb-3" />
            <p className="text-stone-400 text-sm">
              {filter === 'sold' ? 'No sold items yet' : filter === 'active' ? 'No active listings' : 'No listings yet'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {displayProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SellerProfilePage;
